const GA_COOKIE_PREFIXES = ["_ga", "_gid", "_gat"];

const isHttps = (): boolean => {
  return typeof window !== "undefined" && window.location.protocol === "https:";
};

/** Host-only, current subdomain, and `.ui.com` so we clear cookies set on e.g. `.stg.ui.com`. */
const cookieDomainsToTry = (): Array<string | undefined> => {
  if (typeof window === "undefined") {
    return [undefined];
  }
  const host = window.location.hostname;
  const domains = new Set<string | undefined>();
  domains.add(undefined);
  if (host.includes(".")) {
    domains.add(`.${host}`);
  }
  if (host.endsWith(".ui.com") && host !== "ui.com") {
    domains.add(".ui.com");
  }
  return Array.from(domains);
};

const expireCookie = (name: string, domain?: string): void => {
  const secure = isHttps() ? "; Secure" : "";
  const domainPart = domain ? `; domain=${domain}` : "";
  document.cookie = `${encodeURIComponent(name)}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainPart}${secure}`;
};

/**
 * Reddit sets `rdt_uuid` with `SameSite=None; Secure` in many cases; browsers require
 * the same attributes to delete the cookie. Also try Lax / no SameSite for older sets.
 */
const expireRedditCookieWithAttributeVariants = (
  name: string,
  domain?: string,
): void => {
  const encoded = encodeURIComponent(name);
  const domainPart = domain ? `; domain=${domain}` : "";
  const bases = [
    `${encoded}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`,
    `${encoded}=; path=/; Max-Age=0`,
  ];
  for (const base of bases) {
    document.cookie = `${base}${domainPart}`;
    if (isHttps()) {
      document.cookie = `${base}${domainPart}; Secure`;
      document.cookie = `${base}${domainPart}; Secure; SameSite=None`;
      document.cookie = `${base}${domainPart}; Secure; SameSite=Lax`;
    }
  }
};

const clearCookiesMatching = (
  predicate: (name: string) => boolean,
  domains: Array<string | undefined>,
): void => {
  const raw = document.cookie;
  if (!raw) {
    return;
  }
  raw.split(";").forEach((part) => {
    const eq = part.indexOf("=");
    const name = (eq === -1 ? part : part.slice(0, eq)).trim();
    if (!predicate(name)) {
      return;
    }
    for (const d of domains) {
      expireCookie(name, d);
    }
  });
};

/** Best-effort removal of Google Analytics cookies on this host / parent domain. */
export const clearGoogleAnalyticsCookies = (): void => {
  clearCookiesMatching(
    (name) => {
      if (
        GA_COOKIE_PREFIXES.some((p) => name === p || name.startsWith(`${p}_`))
      ) {
        return true;
      }
      return name.startsWith("_ga_");
    },
    cookieDomainsToTry(),
  );
};

const isRedditPixelCookieName = (name: string): boolean => {
  if (name === "rdt_uuid" || name === "_rdt_uuid") {
    return true;
  }
  return name.startsWith("rdt_") || name.startsWith("_rdt_");
};

export const clearRedditPixelCookie = (): void => {
  const raw = document.cookie;
  const names = new Set<string>();
  if (raw) {
    raw.split(";").forEach((part) => {
      const eq = part.indexOf("=");
      const name = (eq === -1 ? part : part.slice(0, eq)).trim();
      if (isRedditPixelCookieName(name)) {
        names.add(name);
      }
    });
  }
  names.add("rdt_uuid");
  names.add("_rdt_uuid");

  const domains = cookieDomainsToTry();
  for (const name of names) {
    for (const d of domains) {
      expireCookie(name, d);
      expireRedditCookieWithAttributeVariants(name, d);
    }
  }
};

/** Hotjar session cookies (`_hjSession_*`, `_hjSessionUser_*`, etc.). */
export const clearHotjarCookies = (): void => {
  clearCookiesMatching(
    (name) => name.startsWith("_hj"),
    cookieDomainsToTry(),
  );
};
